import {
	Button,
	ButtonGroup,
	Dialog,
	IconButton,
	LuIcon,
	radiusCva,
	Separator,
	stopPropagation,
	Text,
	toast,
	useProvider,
} from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, type BulkGeoImagePayload, hashFile } from "@overmap-ai/core"
import { ImageFile } from "@overmap-ai/forms"
import { getRouteApi } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import exifr from "exifr"
import { type ChangeEventHandler, memo, useCallback, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { useFileInputRef, usePagination } from "../../hooks/utils"
import { coordinatesToText } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import type { ExtendableDialogProps } from "./typings"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId")

const computeDegrees = (dmsCoords: [number, number, number]) => {
	return dmsCoords[0] + dmsCoords[1] / 60 + dmsCoords[2] / 3600
}

const requiredEXIFTags = ["GPSLatitude", "GPSLongitude"]
const optionalEXIFTags = ["DateTimeOriginal", "GPSImgDirection"]

type GeoImageUploadDialogProps = ExtendableDialogProps

const ITEMS_PER_PAGE = 5

export const GeoImageUploadDialog = memo<GeoImageUploadDialogProps>((props) => {
	const {
		children,
		modal,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		defaultOpen,
		...rest
	} = props

	const { projectId } = routeApi.useParams()
	const [geoImagePayloads, setGeoImagePayloads] = useState<BulkGeoImagePayload[]>([])
	const [title, setTitle] = useState("")
	const fileHashes = useRef<Set<string>>(new Set())
	const [fileInputRef, toggleFileInput] = useFileInputRef()

	const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (e) => {
			const files = Array.from(e.target.files ?? [])

			const mapImagePayloadAndFile: BulkGeoImagePayload[] = []

			const filesWithoutRequiredEXIFTags: File[] = []
			const alreadyAddedFiles: File[] = []

			for (const file of files) {
				const result = await exifr.parse(file, [...requiredEXIFTags, ...optionalEXIFTags])

				if (!result || requiredEXIFTags.some((tag) => !(tag in result))) {
					filesWithoutRequiredEXIFTags.push(file)
					continue
				}

				const hash = await hashFile(file)

				if (fileHashes.current.has(hash)) {
					alreadyAddedFiles.push(file)
					continue
				} else {
					fileHashes.current.add(hash)
				}

				const latitude = computeDegrees(result.GPSLatitude as [number, number, number])
				const longitude = -1 * computeDegrees(result.GPSLongitude as [number, number, number])

				mapImagePayloadAndFile.push({
					title: file.name,
					description: "",
					geo_marker: turf.point([longitude, latitude] as [number, number]).geometry,
					original_date: (result.DateTimeOriginal as Date)?.toISOString() ?? null,
					direction: (result.GPSImgDirection as number) ?? null,
					file: file,
				})

				if (filesWithoutRequiredEXIFTags.length > 0)
					toast.error({
						title: "Missing EXIF tags",
						// TODO: Offer to create standalone attachments or something
						description: `${filesWithoutRequiredEXIFTags.length} image(s) do not have the required geospatial metadata, and cannot be created.`,
					})

				if (alreadyAddedFiles.length > 0)
					toast.error({
						title: "Already uploaded files",
						description: `All ${alreadyAddedFiles.length} file(s) have already been uploaded.`,
					})
			}

			setGeoImagePayloads((prev) => [...prev, ...mapImagePayloadAndFile])

			if (fileInputRef.current) fileInputRef.current.value = ""
		},
		[fileInputRef],
	)

	const handleRemoveAll = useCallback(() => {
		setGeoImagePayloads([])
		fileHashes.current.clear()
	}, [])

	const { page, currentPage, pageCount, nextPage, prevPage } = usePagination<BulkGeoImagePayload>(
		geoImagePayloads,
		ITEMS_PER_PAGE,
	)

	const handleTitleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
		setTitle(e.target.value)
	}, [])

	const handleBulkCreateGeoImages = useCallback(async () => {
		const updatedPayloads = geoImagePayloads.map((payload) => ({
			...payload,
			title: title || payload.title,
			project: projectId,
		}))

		const [_, promise] = await sdk.geoImages.bulkAdd(updatedPayloads)

		promise
			.then(() => {
				toast.success({
					title: "Images uploaded",
					description: "Images have been uploaded successfully.",
				})
			})
			.catch((error) => {
				toast.error({
					title: "Failed to upload images",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})

		if (!fileInputRef.current) return

		fileInputRef.current.value = ""
		setGeoImagePayloads([])
	}, [fileInputRef, geoImagePayloads, projectId, title])

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{children ? <Dialog.Trigger asChild>{children}</Dialog.Trigger> : null}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content {...rest} title="Upload images" onClick={stopPropagation} className="flex flex-col">
				<div className="flex justify-between gap-2 pb-2">
					<Dialog.Title>
						<Text size="lg">Upload Images</Text>
					</Dialog.Title>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>
				<div className="flex w-full flex-col gap-4 overflow-hidden">
					<div className="flex h-max w-full flex-col gap-4">
						<input
							className="placeholder-(--base-a9) w-full outline-none"
							placeholder="Enter a title"
							value={title}
							onChange={handleTitleChange}
						/>

						<ButtonGroup className="flex h-max w-full justify-between" size="sm">
							<div className="flex items-center gap-2">
								<Button onClick={toggleFileInput} variant="surface">
									<LuIcon icon={modelIconMapping.add} />
									Add images
								</Button>
								<Button variant="fill" onClick={handleRemoveAll} accentColor="base">
									<LuIcon icon={modelIconMapping.delete} />
									Remove all
								</Button>
							</div>
							{pageCount > 0 && (
								<ButtonGroup
									className="flex items-center gap-1"
									size="sm"
									variant="ghost"
									accentColor="base"
								>
									<IconButton onClick={prevPage}>
										<LuIcon icon={modelIconMapping.chevronLeft} />
									</IconButton>
									<Text size="sm" accentColor="base">
										{currentPage + 1} / {pageCount === 0 ? 1 : pageCount}
									</Text>
									<IconButton onClick={nextPage}>
										<LuIcon icon={modelIconMapping.chevronRight} />
									</IconButton>
								</ButtonGroup>
							)}
						</ButtonGroup>
					</div>

					<Separator size="full" />

					<div className="flex w-full grow flex-col gap-2 overflow-y-auto py-1">
						{geoImagePayloads.length > 0 ? (
							page.map((payload, index) => <GeoImageUploadCell key={index} payload={payload} />)
						) : (
							<div className="flex h-[400px] w-full items-center justify-center">
								<Text size="sm" accentColor="base">
									Add images to upload
								</Text>
							</div>
						)}
					</div>

					<Separator size="full" />

					<div className="flex items-center justify-between">
						<Text size="sm" accentColor="base">
							{geoImagePayloads.length} to upload
						</Text>
						<Dialog.Close asChild>
							<Button
								size="sm"
								variant="surface"
								accentColor="primary"
								onClick={handleBulkCreateGeoImages}
							>
								<LuIcon icon={modelIconMapping.check} />
								Submit
							</Button>
						</Dialog.Close>
					</div>
				</div>
				<input
					className="hidden"
					ref={fileInputRef}
					type="file"
					accept="image/*"
					multiple
					onChange={handleInputChange}
				/>
			</Dialog.Content>
		</Dialog.Root>
	)
})
GeoImageUploadDialog.displayName = "GeoImageUploadDialog"

interface GeoImageUploadCell {
	payload: BulkGeoImagePayload
}

function GeoImageUploadCell(props: GeoImageUploadCell) {
	const { payload } = props
	const { radius } = useProvider()

	return (
		<div
			className={twMerge(
				cx(
					"border-(--base-a6) group relative flex h-[300px] w-full shrink-0 select-none flex-col overflow-hidden border",
					radiusCva({ radius, maxLarge: true }),
				),
			)}
		>
			<div className="flex grow items-center justify-center overflow-hidden bg-clip-padding">
				<ImageFile className="max-w-full object-cover" file={payload.file} />
			</div>
			<div className="bg-(--base-a2) flex h-max w-full shrink-0 justify-between p-4">
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<LuIcon icon={modelIconMapping.location} />
						<Text size="sm" accentColor="base">
							{coordinatesToText(payload.geo_marker?.coordinates, 4)}
						</Text>
					</div>
					{!!payload.original_date && (
						<div className="flex items-center gap-2">
							<LuIcon icon={modelIconMapping.dueDate} />
							<Text size="sm" accentColor="base">
								{new Date(payload.original_date).toLocaleString(undefined, {
									month: "long",
									day: "numeric",
									year: "numeric",
									hour: "numeric",
									minute: "numeric",
								})}
							</Text>
						</div>
					)}
				</div>
				<IconButton
					className="opacity-100 transition-all group-hover:opacity-100 lg:opacity-0"
					size="sm"
					variant="ghost"
				>
					<LuIcon icon={modelIconMapping.delete} />
				</IconButton>
			</div>
		</div>
	)
}
GeoImageUploadCell.displayName = "GeoImageUploadCell"
